import React, { lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BottomSheet from './BottomSheet';
import { useMovieQuickView } from '../../contexts/MovieQuickViewContext';
import { useAuth } from '../../contexts/AuthContext';

const LazyMovieDetail = lazy(() => import('../pages/MovieDetail'));

/**
 * Global movie quick-view sheet. Renders once in AppLayout.
 * On mobile: BottomSheet at 92dvh with swipe-up-to-full-page.
 * On desktop: navigates directly to the full movie page (replacing the _mqv
 * history entry so back navigation returns to the originating page cleanly).
 *
 * The list page behind the sheet stays mounted → no scroll position loss.
 */
const MovieQuickViewSheet: React.FC = () => {
  const { quickViewMovieId, closeMovieQuickView, openMovieQuickView, hideSheetForNavigation } = useMovieQuickView();
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // On desktop: replace the _mqv history entry with the full movie page so that
  // back navigation returns to the originating page, not to an empty _mqv state.
  useEffect(() => {
    if (!isMobile && quickViewMovieId) {
      navigate(`/movie/${quickViewMovieId}`, { replace: true });
    }
  }, [isMobile, quickViewMovieId, navigate]);

  // Persist the last known movieId so MovieDetail stays mounted when the sheet hides
  // for navigation (hideSheetForNavigation sets quickViewMovieId → null, but we want
  // to keep the component alive so the user doesn't see a full reload on back-nav).
  const lastMovieIdRef = useRef<string | null>(null);
  if (quickViewMovieId) lastMovieIdRef.current = quickViewMovieId;
  const mountedMovieId = lastMovieIdRef.current;

  const handleViewFullPage = useCallback(() => {
    const id = lastMovieIdRef.current;
    if (!id) return;
    // Replace the _mqv sheet history entry with the full movie page so that
    // pressing back returns to the originating page, not back to the sheet.
    navigate(`/movie/${id}`, { replace: true });
  }, [navigate]);

  const handlePersonSelect = useCallback((personId: string) => {
    hideSheetForNavigation();
    navigate(`/person/${personId}`);
  }, [hideSheetForNavigation, navigate]);

  const handleUserSelect = useCallback((userId: string) => {
    hideSheetForNavigation();
    navigate(`/profile/${userId}`);
  }, [hideSheetForNavigation, navigate]);

  // Desktop navigates away — nothing to render here.
  if (!isMobile) {
    return null;
  }

  const movieDetailContent = mountedMovieId ? (
    <Suspense fallback={
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
        <CircularProgress size={40} />
      </Box>
    }>
      <LazyMovieDetail
        movieId={mountedMovieId}
        onBack={closeMovieQuickView}
        onMovieSelect={openMovieQuickView}
        onPersonSelect={handlePersonSelect}
        onUserSelect={handleUserSelect}
        onViewFullPage={handleViewFullPage}
        user={user || undefined}
        isSheetMode
      />
    </Suspense>
  ) : null;

  return (
    <BottomSheet
      open={!!quickViewMovieId}
      onClose={closeMovieQuickView}
      height="92dvh"
      onSwipeUpPastMax={handleViewFullPage}
      zIndex={1250}
      modalProps={{ keepMounted: true }}
      disableWebHistoryBack
    >
      {/* Scrollable inner container — MovieDetail renders inside this, not window */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          // iOS safe-area for home indicator at bottom
          pb: 'env(safe-area-inset-bottom)',
          // Allow inner scroll; pull-down-to-close fires from BottomSheet content handler
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {movieDetailContent}
      </Box>
    </BottomSheet>
  );
};

export default MovieQuickViewSheet;
